import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { PALETTE } from '../theme';

/**
 * Generic searchable single-select picker.
 *
 * Accepts the same `options` / `groups` shape that the legacy SelectDropdown
 * takes, so it drops in next to existing call sites with minimal churn:
 *
 *   <SearchablePicker
 *     value={mapType}
 *     onChange={setMapType}
 *     groups={MAP_TYPE_GROUPS}
 *     label="Travel Map"
 *     placeholder='Search regions… (try "japan")'
 *   />
 *
 *   <SearchablePicker
 *     value={shape}
 *     onChange={setShape}
 *     options={shapeOptions}    // flat list, no group headers
 *     label="Map Shape"
 *     placeholder="Search 100+ shapes…"
 *   />
 *
 * Typing matches against the option `text` field (MUI's default filter is
 * case-insensitive substring), so "japan" surfaces "🇯🇵 Japan (Prefectures)".
 */
const SearchablePicker = ({
    value,
    onChange,
    options,
    groups,
    label,
    placeholder,
    disabled = false,
    sx = {}
}) => {
    // Normalise grouped + flat input into a single annotated option list so
    // MUI can render either a flat or grouped dropdown via `groupBy`.
    const allOptions = React.useMemo(() => {
        if (groups && groups.length) {
            const flat = [];
            for (const g of groups) {
                for (const it of g.items || []) {
                    flat.push({ ...it, group: g.label });
                }
            }
            return flat;
        }
        return (options || []).map((o) => ({ ...o }));
    }, [options, groups]);

    const useGrouping = Boolean(groups && groups.length);
    const selectedOption = allOptions.find((o) => o.value === value) || null;

    return (
        <Autocomplete
            value={selectedOption}
            onChange={(_e, opt) => opt && onChange(opt.value)}
            options={allOptions}
            groupBy={useGrouping ? (opt) => opt.group : undefined}
            getOptionLabel={(opt) => opt.text || ''}
            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
            disabled={disabled}
            disableClearable
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <Search sx={{ fontSize: 18, color: PALETTE.textMuted, mr: 0.75, ml: 0.25 }} />
                        )
                    }}
                />
            )}
            renderOption={(props, opt) => {
                // MUI v7 puts `key` inside the props object; React forbids
                // spreading a `key`, so pull it out and pass it explicitly.
                const { key, ...rest } = props;
                return (
                    <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.95rem', color: PALETTE.text }}>
                            {opt.text}
                        </Typography>
                    </Box>
                );
            }}
            sx={{
                width: '100%',
                ...sx,
                '& .MuiOutlinedInput-root': { backgroundColor: PALETTE.surface }
            }}
        />
    );
};

SearchablePicker.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.array,
    groups: PropTypes.array,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    sx: PropTypes.object
};

export default SearchablePicker;
